import React, { useEffect, useRef, useState, useCallback } from "react";
import { Sparkles } from "lucide-react";
import { APP_VERSION } from "@/version";

/**
 * PWAUpdatePrompt
 * ---------------
 * Strategy for detecting and applying a new deployment while the PWA is
 * installed:
 *
 * 1. Register /sw.js and keep the registration handle.
 * 2. Poll /version.json every 60s (and on window focus / online events).
 *    If the server's version differs from our embedded APP_VERSION, we
 *    know a new deployment exists. We then call registration.update()
 *    which asks the browser to fetch the latest /sw.js.
 * 3. When a new SW moves to the "installed" state AND there is already a
 *    controlling SW, a true update is pending. sw.js's own install handler
 *    now calls skipWaiting() itself, so this applies automatically --
 *    nobody has to notice a banner and tap a button. We just show a brief
 *    "Updating..." notice so a sudden reload doesn't look like a glitch.
 * 4. Once the new SW takes control (controllerchange), reload exactly
 *    once so the user is guaranteed to be running the latest code.
 *
 * This auto-apply behaviour matters most for an installed iOS PWA:
 * force-quitting and reopening the app does NOT clear its cached files or
 * service worker (only the process restarts, not the storage), so a
 * manual-click update was easy to get permanently stuck behind.
 */

const VERSION_POLL_INTERVAL_MS = 60 * 1000;

const PWAUpdatePrompt: React.FC = () => {
  const [updating, setUpdating] = useState(false);

  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const reloadingRef = useRef(false);

  /** A new SW is ready -- it already called skipWaiting() itself, so just
   *  show a brief notice; the controllerchange listener below reloads
   *  once it actually takes over. */
  const promoteWaiting = useCallback((worker: ServiceWorker | null) => {
    if (!worker) return;
    setUpdating(true);
    // Safety net in case controllerchange never fires (e.g. this worker
    // was somehow already superseded) -- force a reload regardless.
    setTimeout(() => {
      if (!reloadingRef.current) {
        reloadingRef.current = true;
        window.location.reload();
      }
    }, 3000);
  }, []);

  /** Poll /version.json; if changed, ask SW to update. */
  const checkForUpdate = useCallback(async () => {
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-store",
      });
      if (!res.ok) return;
      const data = (await res.json()) as { version?: string };
      if (data?.version) {
        if (data.version !== APP_VERSION) {
          // Server has newer code; ask the browser to fetch the new SW.
          try {
            await registrationRef.current?.update();
          } catch {
            /* ignore network errors */
          }
          // If a new worker is already waiting, surface immediately.
          if (registrationRef.current?.waiting) {
            promoteWaiting(registrationRef.current.waiting);
          }
        }
      }
    } catch {
      /* offline or not deployed yet — ignore */
    }
  }, [promoteWaiting]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    let pollTimer: number | undefined;

    const register = async () => {
      try {
        const registration = await navigator.serviceWorker.register("/sw.js");
        registrationRef.current = registration;

        // If there is already a waiting worker when we register (e.g. user
        // reopened the app), show the prompt right away.
        if (registration.waiting && navigator.serviceWorker.controller) {
          promoteWaiting(registration.waiting);
        }

        // Listen for a new worker being installed.
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              promoteWaiting(newWorker);
            }
          });
        });

        // After the user triggers SKIP_WAITING and the new SW takes
        // control, reload exactly once.
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (reloadingRef.current) return;
          reloadingRef.current = true;
          window.location.reload();
        });

        // Kick off polling for version changes.
        checkForUpdate();
        pollTimer = window.setInterval(
          checkForUpdate,
          VERSION_POLL_INTERVAL_MS
        );
      } catch (err) {
        console.log("[PWAUpdatePrompt] SW registration failed:", err);
      }
    };

    register();

    // Re-check when the tab regains focus or connectivity is restored —
    // these are the most common moments an installed PWA becomes stale.
    const onFocus = () => checkForUpdate();
    const onOnline = () => checkForUpdate();
    const onVisibility = () => {
      if (document.visibilityState === "visible") checkForUpdate();
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (pollTimer) window.clearInterval(pollTimer);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [checkForUpdate, promoteWaiting]);

  if (!updating) return null;

  // No buttons -- nothing for the user to notice or click. sw.js's own
  // install handler already called skipWaiting(); this is purely a
  // heads-up so the reload that follows in a moment doesn't look like an
  // unexplained glitch.
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] w-[calc(100%-2rem)] max-w-xs animate-in slide-in-from-bottom-4"
    >
      <div className="rounded-xl border border-orange-200 bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center gap-3 p-3.5">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-orange-500" />
          </div>
          <p className="text-sm font-medium text-gray-900">
            Updating to the latest version&hellip;
          </p>
        </div>
      </div>
    </div>
  );
};

export default PWAUpdatePrompt;
